import useWindow from '@/hooks/useWindow';
import { Button, Chip, FormControlLabel, Stack, Switch, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { ApiResponse, err } from '../api/axios';
import {
  createDataSource,
  getAllDataSources,
  getAllOrganisations,
  reloadProxyModels,
  setDataSourceDeleted,
  updateDataSource,
} from '../api/super-admin';
import { DataSource } from '../core/data-sources';
import { OrganisationDetails } from '../core/global';
import { dataSourceShape } from '../data/form-shapes';
import { fromFormValues, toFormValues } from '../helpers/data-source-form-values';
import styles from "../styles/components/super-admin-settings-data-sources.module.scss";
import { ConfirmDeleteModal } from './confirm-delete-modal';
import { CopyButton } from './copy-button';
import { ErrorMessage } from './error-message';
import { CreateForm, FormObject } from './form-components';
import { DataSourceChildrenModal, DataSourceTypesModal } from './super-admin-data-source-children-modal';

type EditTarget = number | 'new' | null;

export const SuperAdminSettingsDataSources = () => {
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [apiError, setApiError] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);

  const [editTarget, setEditTarget] = useState<EditTarget>(null);
  const [editDefaults, setEditDefaults] = useState<Record<string, any> | null>(null);

  const [childrenTarget, setChildrenTarget] = useState<{ row: DataSource; tab: number } | null>(null);
  const [showTypes, setShowTypes] = useState(false);

  // Owner organisations are a multi-select, so the edit form needs the list.
  const [organisations, setOrganisations] = useState<OrganisationDetails[]>([]);

  const [deleteTarget, setDeleteTarget] = useState<DataSource | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Data-source edits only take effect in the Python proxy after it reloads its
  // in-memory models. Any successful change marks the proxy "stale"; the
  // "Refresh proxy" button is enabled while stale and clears the flag on success.
  const [proxyStale, setProxyStale] = useState(false);
  const [reloadingProxy, setReloadingProxy] = useState(false);

  const fetchDataSources = useCallback(async () => {
    setLoading(true);
    const res = await getAllDataSources({ include_deleted: showDeleted });
    const { error, response, message } = res;

    if (error === 200 && response) {
      setDataSources(response?.results || []);
      setApiError([]);
    } else {
      setApiError([message || 'An error occurred while fetching data sources.']);
    }
    setLoading(false);
  }, [showDeleted]);

  // fetchDataSources is in the dep list so flipping "Include deleted" refetches
  // on its own rather than waiting for a manual refresh.
  useEffect(() => {
    fetchDataSources();
  }, [fetchDataSources]);

  useEffect(() => {
    const execute = async () => {
      const { error, response } = await getAllOrganisations();
      if (error === 200 && response) setOrganisations(response?.results || []);
    };
    execute();
  }, []);

  useEffect(() => {
    if (editTarget === 'new') {
      setEditDefaults(toFormValues(null));
      return;
    }
    if (editTarget !== null && dataSources.length > 0) {
      const found = dataSources.find((d) => d.id === editTarget);
      setEditDefaults(found ? toFormValues(found) : null);
    } else {
      setEditDefaults(null);
    }
  }, [editTarget, dataSources]);

  const setDeleted = useCallback(async (row: DataSource, deleted: boolean) => {
    const { error, message } = await setDataSourceDeleted(row.id, deleted);
    if (error === 200) {
      setApiError([]);
      setProxyStale(true);   // data-source deleted/restored — proxy needs reload
      fetchDataSources();
      return true;
    }
    setApiError([message || `Couldn't ${deleted ? 'delete' : 'restore'} data source ${row.id}.`]);
    return false;
  }, [fetchDataSources]);

  // Ask the backend to reload the proxy's in-memory models so saved changes go
  // live. On success, clear the stale flag.
  const reloadProxy = useCallback(async () => {
    setReloadingProxy(true);
    setApiError([]);
    const { error, message } = await reloadProxyModels();
    setReloadingProxy(false);
    if (error < 300) {
      setProxyStale(false);
    } else {
      setApiError([message || 'Failed to refresh the proxy. Changes may not be live yet.']);
    }
  }, []);

  // Restoring is harmless and reversible, so it stays a single click.
  const handleRestore = useCallback((row: DataSource) => {
    setDeleted(row, false);
  }, [setDeleted]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const ok = await setDeleted(deleteTarget, true);
    setDeleting(false);
    if (ok) setDeleteTarget(null);
  }, [deleteTarget, setDeleted]);

  // Short identity label for the confirmation message.
  const deleteLabel = deleteTarget
    ? [deleteTarget.provider, deleteTarget.model, deleteTarget.geography]
        .filter(Boolean)
        .join('/') || `data-source-${deleteTarget.id}`
    : '';

  const _window = useWindow();

  const columns: any = [
    {
      field: 'id',
      headerName: 'ID',
      type: 'number',
      width: 70,
      editable: false,
    },
    {
      field: 'provider',
      headerName: 'Provider',
      width: 120,
      editable: false,
    },
    {
      field: 'model',
      headerName: 'Model',
      width: 150,
      editable: false,
    },
    {
      field: 'geography',
      headerName: 'Geography',
      width: 130,
      editable: false,
    },
    {
      field: 'version',
      headerName: 'Version',
      width: 90,
      editable: false,
    },
    {
      field: 'full_name',
      headerName: 'Display name',
      width: 220,
      editable: false,
    },
    {
      field: 'table_name',
      headerName: 'Table',
      width: 200,
      editable: false,
      renderCell: (params: any) => {
        const value = params?.row?.table_name;
        if (!value) return '—';
        return (
          <span>
            {value}
            <CopyButton content={value} />
          </span>
        );
      },
    },
    {
      field: 'source',
      headerName: 'Backend',
      width: 100,
      editable: false,
    },
    {
      field: 'format',
      headerName: 'Format',
      width: 90,
      editable: false,
    },
    {
      field: 'coverage',
      headerName: 'Coverage',
      width: 190,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: (params: any) => {
        const r = params?.row || {};
        const fmt = (v: string | null) => (v ? format(new Date(v), 'dd/MM/yy') : '?');
        if (!r.start_date && !r.end_date) return '—';
        return `${fmt(r.start_date)} – ${fmt(r.end_date)}`;
      },
    },
    {
      field: 'resolution',
      headerName: 'Resolution',
      width: 110,
      editable: false,
    },
    {
      field: 'frequency',
      headerName: 'Freq (h)',
      type: 'number',
      width: 90,
      editable: false,
    },
    {
      field: 'tokens_cost_per_year',
      headerName: 'Tokens/yr',
      type: 'number',
      width: 100,
      editable: false,
    },
    {
      field: 'is_private',
      headerName: 'Private',
      type: 'boolean',
      width: 80,
      editable: false,
    },
    {
      field: 'requires_login',
      headerName: 'Login',
      type: 'boolean',
      width: 80,
      editable: false,
    },
    {
      field: 'regular',
      headerName: 'Regular',
      type: 'boolean',
      width: 80,
      editable: false,
    },
    {
      field: 'has_averages',
      headerName: 'Averages',
      type: 'boolean',
      width: 90,
      editable: false,
    },
    {
      field: 'checks',
      headerName: 'Valid record',
      width: 170,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: (params: any) => {
        const r: DataSource = params?.row || {};
        const problems: string[] = [];
        if (r.regular && r.table_name && !r.filename_prefix) problems.push('No filename prefix');
        if (r.has_bounds === false) problems.push('No bounds');
        if (r.source === 'azure' && !r.access_token_name) problems.push('No access token');

        if (r.deleted) return <Chip size="small" variant="outlined" label="Deleted" />;
        if (problems.length === 0) return <Chip size="small" color="success" label="OK" />;
        return (
          <Tooltip title={problems.join(' · ')}>
            <Chip size="small" color="warning" label={problems[0]} />
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 420,
      sortable: false,
      filterable: false,
      editable: false,
      cellClassName: styles.table,
      renderCell: (params: any) => {
        const row: DataSource = params?.row;
        return (
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setEditTarget(row.id)}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setChildrenTarget({ row, tab: 0 })}
            >
              {`Layers${row.layers_count !== undefined ? ` (${row.layers_count})` : ''}`}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setChildrenTarget({ row, tab: 1 })}
            >
              {`Vectors${row.vectors_count !== undefined ? ` (${row.vectors_count})` : ''}`}
            </Button>
            <Button
              variant="text"
              color={row.deleted ? 'primary' : 'error'}
              size="small"
              onClick={() => (row.deleted ? handleRestore(row) : setDeleteTarget(row))}
            >
              {row.deleted ? 'Restore' : 'Delete'}
            </Button>
          </Stack>
        );
      },
    },
  ];

  return (
    <div>
      <ErrorMessage message={apiError} />

      <div className={styles.buttonRow}>
        <div>
          <Button variant="contained" size="small" onClick={() => setShowTypes(true)}>
            Show vector types
          </Button>
          <Button variant="contained" size="small" onClick={() => setEditTarget('new')}>
            Add data source
          </Button>
          <FormControlLabel
            control={
              <Switch checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />
            }
            label="Include deleted"
          />
          <Button variant="outlined" size="small" onClick={fetchDataSources} disabled={loading}>
            {loading ? 'Loading…' : 'Reload data sources'}
          </Button>
          <Button
            variant="contained"
            color="warning"
            size="small"
            onClick={reloadProxy}
            // disabled={!proxyStale || reloadingProxy}
          >
            {reloadingProxy ? 'Refreshing...' : 'Refresh proxy'}
          </Button>
        </div>  
        <div>
            <Button
              color="error"
              onClick={() => {                 
                _window.setTimeout(() => {
                  _window.location.reload();
                }, 100)
              }}
            >
              Page refresh
            </Button>
        </div>
      </div>

      <DataGrid
        rows={dataSources}
        columns={columns}
        getRowId={(row) => row.id}
        loading={loading}
        getRowClassName={(params) => (params.row.deleted ? 'row-deleted' : '')}
        initialState={{
          sorting: {
            sortModel: [{ field: 'provider', sort: 'asc' }],
          },
          pagination: {
            paginationModel: {
              pageSize: 25,
            },
          },
        }}

        pageSizeOptions={[25, 100, 200]}
        disableRowSelectionOnClick
        sx={{ '& .row-deleted': { opacity: 0.5 } }}
      />

      {editTarget !== null && editDefaults && (
        <CreateForm
          onCancel={() => setEditTarget(null)}
          title={editTarget === 'new' ? 'Add data source' : `Edit data source #${editTarget}`}
          intro={
            'Provider, model and geography identify the source everywhere else in the app — changing them changes how stored files are resolved.'
          }
          size="medium"
          allowEdit={true}
          shape={dataSourceShape(organisations)}
          defaultValues={editDefaults}
          isModal
          onSuccess={() => {
            setEditTarget(null);
            setProxyStale(true);   // data-source changed — proxy needs reload
            fetchDataSources();
          }}
          onSubmit={async (vals: FormObject): Promise<ApiResponse> => {
            const { payload, errors } = fromFormValues(vals);
            if (errors.length > 0) {
              return err(errors.join(' '), 400);
            }
            return editTarget === 'new'
              ? createDataSource(payload)
              : updateDataSource(editTarget as number, payload);
          }}
        />
      )}

      {childrenTarget && (
        <DataSourceChildrenModal
          dataSource={childrenTarget.row}
          initialTab={childrenTarget.tab}
          handleClose={() => {
            setChildrenTarget(null);
            setProxyStale(true);   // layers/types/vectors changed — proxy needs reload
            fetchDataSources();
          }}
        />
      )}

      {showTypes && <DataSourceTypesModal handleClose={() => setShowTypes(false)} />}

      {deleteTarget && (
        <ConfirmDeleteModal
          title={`Delete data source #${deleteTarget.id}`}
          confirmLabel="Delete data source"
          busy={deleting}
          intro={
            <>
              {deleteTarget.full_name || deleteLabel} will stop being offered to users
              and stop resolving to stored files.
            </>
          }
          warnings={[
            'The row stays in the database and can be restored from this screen.',
            `${deleteTarget.layers_count ?? 'Its'} layers and ${deleteTarget.vectors_count ?? 'its'} vectors stay attached.`,
            'Orders already placed against it keep their stored data.',
          ]}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </div>
  );
};